import { isKnownLinkKind } from "@/features/feed/contract"
import type { FeedLink, FeedPageType, FeedSavedQuery, PageTarget } from "@/features/feed/contract"
import { buildRoute, localizeHref, ROUTES } from "@/lib/routes"
import type { SearchQuery } from "@/lib/types/search"
import { logger } from "@/platform/observability/logger"

export const PAGE_PATHS: Record<PageTarget, { path: string; localized: boolean }> = {
  REFERRALS: { path: buildRoute.referrals(), localized: true },
  HELP: { path: "https://www.nelo.mx/es/preguntas-frecuentes", localized: false },
  SELL_WITH_NELO: { path: "https://www.nelo.mx/es/vende-en-nelo", localized: false },
  HOW_IT_WORKS: { path: `/${ROUTES.HOW_TO_BUY}`, localized: true },
  CATEGORIES: { path: `/${ROUTES.CATEGORIES}`, localized: true },
}

// The feed wire query (minPrice/maxPrice/sortBy) is deliberately not the FE
// SearchQuery (priceMin/priceMax/sort) — this adapter owns that mapping.
export const feedQueryToSearchQuery = (query: FeedSavedQuery | undefined): Partial<SearchQuery> => {
  const mapped: Partial<SearchQuery> = {}
  if (!query) return mapped
  if (query.categories?.length) mapped.categories = query.categories
  if (query.collections?.length) mapped.collections = query.collections
  if (query.minPrice !== undefined) mapped.priceMin = query.minPrice
  if (query.maxPrice !== undefined) mapped.priceMax = query.maxPrice
  if (query.inStock !== undefined) mapped.inStockOnly = query.inStock
  if (query.sortBy) mapped.sort = query.sortBy
  if (query.limit) mapped.limit = query.limit
  return mapped
}

export const localizePath = localizeHref

interface SelfPage {
  pageType: FeedPageType
  handle: string
}

// A saved query that's nothing but a single category (or collection) handle browses that
// facet — route it to the category/collection page rather than /busqueda. The search page
// derives its client query from the URL and only resolves handles→ids server-side, so a
// handle left in the query string breaks any client refetch (load-more, filter, sort) with
// a 400; the facet routes carry the handle as a path segment and don't hit that. Queries
// that also carry price/stock/sort or combine facets stay on /busqueda.
const isCatalogId = (value: string): boolean => /^\d+$/.test(value)

const savedQueryToFacetRoute = (query: FeedSavedQuery | undefined): string | undefined => {
  const categories = query?.categories ?? []
  const collections = query?.collections ?? []
  const [category] = categories
  const [collection] = collections
  if (category && categories.length === 1 && collections.length === 0 && !isCatalogId(category)) return buildRoute.category(category)
  if (collection && collections.length === 1 && categories.length === 0 && !isCatalogId(collection)) return buildRoute.collection(collection)
  return undefined
}

// A page's own facet, authored as a link to itself — "Ver toda la electrónica" on the
// electronics page. The page is the listing, so that link is the listing; a filtered one
// keeps its price/stock/sort there, since the listing reads them from the URL.
const isSelfFacet = (route: string, self: SelfPage | undefined): boolean =>
  self !== undefined &&
  ((self.pageType === "CATEGORY" && route === buildRoute.category(self.handle)) || (self.pageType === "COLLECTION" && route === buildRoute.collection(self.handle)))

const selfListingRoute = (route: string, query: FeedSavedQuery | undefined): string => {
  const filters = feedQueryToSearchQuery(query)
  delete filters.categories
  delete filters.collections
  const params = buildRoute.search(filters).split("?")[1]
  return params ? `${route}?${params}` : route
}

export const resolveFeedLink = (link: FeedLink, countryCode: string, self?: SelfPage): string | undefined => {
  const localize = (route: string): string => localizePath(route, countryCode)
  switch (link.kind) {
    case "CATEGORY":
      return link.handle.trim() ? localize(buildRoute.category(link.handle)) : undefined
    case "COLLECTION":
      return link.handle.trim() ? localize(buildRoute.collection(link.handle)) : undefined
    case "PRODUCT":
      return link.handle.trim() ? localizePath(buildRoute.product(link.handle), countryCode) : undefined
    case "SEARCH": {
      const facetRoute = savedQueryToFacetRoute(link.query)
      if (facetRoute && isSelfFacet(facetRoute, self)) return localizePath(selfListingRoute(facetRoute, link.query), countryCode)
      return localize(facetRoute ?? buildRoute.search(feedQueryToSearchQuery(link.query)))
    }
    case "PAGE": {
      const target = PAGE_PATHS[link.page]
      if (!target) return undefined
      return target.localized ? localizePath(target.path, countryCode) : target.path
    }
    case "EXTERNAL":
      if (link.url.startsWith("https://")) return link.url
      logger.info("feed.link.dropped_insecure_external", { url: link.url })
      return undefined
    default: {
      const { kind } = link as { kind: string }
      if (!isKnownLinkKind(kind)) logger.info("feed.link.unknown_kind", { kind })
      return undefined
    }
  }
}

const SEE_ALL_FALLBACK: Record<string, string> = {
  DROP_RAIL: `/${ROUTES.SEARCH}`,
  PRODUCT_CAROUSEL: `/${ROUTES.SEARCH}`,
}

export const resolveSeeAllLink = (seeAll: FeedLink | undefined, sectionType: string, countryCode: string, self?: SelfPage): string | undefined => {
  const resolved = seeAll ? resolveFeedLink(seeAll, countryCode, self) : undefined
  if (resolved) return resolved
  const fallback = SEE_ALL_FALLBACK[sectionType]
  return fallback ? localizePath(fallback, countryCode) : undefined
}
